/**
 * FSRS-Inspired Spaced Repetition Scheduler
 *
 * Per-item-per-user tracking with stability, difficulty, lapse count,
 * and review timestamps. Prioritizes items where retention probability
 * drops below threshold.
 *
 * Rating derivation: correctness + response time + difficulty
 *   → {Again (1), Hard (2), Good (3), Easy (4)}
 *   → stability multipliers (×0.5, ×1.2, ×2.5, ×3.5)
 *
 * Based on: FSRS (Piotr Wozniak / open-spaced-repetition), SM-2 heritage
 *
 * Item states: new → learning → review ↔ relearning
 */

const MS_PER_DAY = 86400000;

// Default parameters
const INITIAL_STABILITY = 1.0;      // 1 day initial stability for new items
const INITIAL_DIFFICULTY = 0.5;     // mid-range difficulty
const TARGET_RETENTION = 0.85;      // review when R drops below this
const LEARNING_STEPS_MS = [600000, 3600000]; // 10 min, 1 hour (learning intervals)

/**
 * Rating constants
 */
export const Rating = {
    AGAIN: 1,
    HARD: 2,
    GOOD: 3,
    EASY: 4
};

/**
 * Item state constants
 */
export const ItemState = {
    NEW: 'new',
    LEARNING: 'learning',
    REVIEW: 'review',
    RELEARNING: 'relearning'
};

/**
 * Create a new item history record for a first-seen item
 *
 * @param {string} itemId - The question ID
 * @param {string} domain - The domain of the question
 * @returns {object} New item history record
 */
export function createNewItemRecord(itemId, domain) {
    return {
        itemId,
        domain,
        firstSeen: Date.now(),
        lastReviewed: null,
        nextReviewDue: null,
        stability: INITIAL_STABILITY,
        difficulty: INITIAL_DIFFICULTY,
        lapseCount: 0,
        reviewCount: 0,
        lastRating: null,
        state: ItemState.NEW,
        learningStep: 0  // index into LEARNING_STEPS_MS
    };
}

/**
 * Derive a rating from response data
 *
 * @param {boolean} correct - Whether the answer was correct
 * @param {number|null} responseTimeMs - Response time in milliseconds
 * @param {number} difficulty - Item difficulty (author scale, 0-0.9)
 * @returns {number} Rating: 1 (Again), 2 (Hard), 3 (Good), 4 (Easy)
 */
export function deriveRating(correct, responseTimeMs, difficulty) {
    if (!correct) return Rating.AGAIN;

    if (!responseTimeMs) return Rating.GOOD;

    // Heuristic median time based on difficulty
    const estimatedMedianMs = 20000 + difficulty * 40000;
    const fastThreshold = estimatedMedianMs * 0.7;
    const slowThreshold = estimatedMedianMs * 1.5;

    if (responseTimeMs < fastThreshold) return Rating.EASY;
    if (responseTimeMs > slowThreshold) return Rating.HARD;
    return Rating.GOOD;
}

/**
 * Compute retention probability for an item at a given time
 *
 * R(t) = e^(-t/S) where t = elapsed days, S = stability in days
 *
 * @param {number} stability - Current stability in days
 * @param {number} elapsedDays - Days since last review
 * @returns {number} Retention probability [0, 1]
 */
export function getRetention(stability, elapsedDays) {
    if (stability <= 0 || elapsedDays <= 0) return 1.0;
    return Math.exp(-elapsedDays / stability);
}

/**
 * Update an item record based on a review
 *
 * @param {object} record - Current item history record
 * @param {number} rating - Rating (1-4)
 * @returns {object} Updated item history record (new object, does not mutate input)
 */
export function updateItemRecord(record, rating) {
    const updated = { ...record };
    updated.reviewCount = (updated.reviewCount || 0) + 1;
    updated.lastRating = rating;
    updated.lastReviewed = Date.now();

    switch (updated.state) {
        case ItemState.NEW:
        case ItemState.LEARNING:
            return handleLearningUpdate(updated, rating);
        case ItemState.REVIEW:
            return handleReviewUpdate(updated, rating);
        case ItemState.RELEARNING:
            return handleRelearningUpdate(updated, rating);
        default:
            return handleLearningUpdate(updated, rating);
    }
}

/**
 * Handle update for items in learning state
 */
function handleLearningUpdate(record, rating) {
    if (rating === Rating.AGAIN) {
        // Reset to beginning of learning steps
        record.learningStep = 0;
        record.state = ItemState.LEARNING;
        record.nextReviewDue = Date.now() + LEARNING_STEPS_MS[0];
        return record;
    }

    if (rating === Rating.EASY) {
        // Graduate immediately to review with boosted stability
        record.state = ItemState.REVIEW;
        record.stability = INITIAL_STABILITY * 3.5;
        record.nextReviewDue = Date.now() + record.stability * MS_PER_DAY;
        return record;
    }

    // Good or Hard: advance through learning steps
    const nextStep = (record.learningStep || 0) + 1;

    if (nextStep >= LEARNING_STEPS_MS.length) {
        // Graduate to review
        record.state = ItemState.REVIEW;
        record.stability = rating === Rating.GOOD ? INITIAL_STABILITY * 2.5 : INITIAL_STABILITY * 1.2;
        record.nextReviewDue = Date.now() + record.stability * MS_PER_DAY;
    } else {
        // Move to next learning step
        record.learningStep = nextStep;
        record.state = ItemState.LEARNING;
        record.nextReviewDue = Date.now() + LEARNING_STEPS_MS[nextStep];
    }

    return record;
}

/**
 * Handle update for items in review state
 */
function handleReviewUpdate(record, rating) {
    if (rating === Rating.AGAIN) {
        // Lapse: enter relearning
        record.lapseCount = (record.lapseCount || 0) + 1;
        record.state = ItemState.RELEARNING;
        record.stability = Math.max(0.5, record.stability * 0.5);
        record.learningStep = 0;
        record.nextReviewDue = Date.now() + LEARNING_STEPS_MS[0];

        // Increase item difficulty on lapse
        record.difficulty = Math.min(1.0, (record.difficulty || 0.5) + 0.1);
        return record;
    }

    // Successful review: multiply stability
    const multipliers = {
        [Rating.HARD]: 1.2,
        [Rating.GOOD]: 2.5,
        [Rating.EASY]: 3.5
    };

    const multiplier = multipliers[rating] || 2.5;
    record.stability = record.stability * multiplier;

    // Adjust difficulty based on rating
    if (rating === Rating.EASY) {
        record.difficulty = Math.max(0.0, (record.difficulty || 0.5) - 0.05);
    } else if (rating === Rating.HARD) {
        record.difficulty = Math.min(1.0, (record.difficulty || 0.5) + 0.05);
    }

    record.nextReviewDue = Date.now() + record.stability * MS_PER_DAY;
    return record;
}

/**
 * Handle update for items in relearning state
 */
function handleRelearningUpdate(record, rating) {
    if (rating === Rating.AGAIN) {
        record.learningStep = 0;
        record.nextReviewDue = Date.now() + LEARNING_STEPS_MS[0];
        return record;
    }

    if (rating === Rating.EASY) {
        // Graduate back to review with moderate stability
        record.state = ItemState.REVIEW;
        record.stability = Math.max(1.0, record.stability * 2.0);
        record.nextReviewDue = Date.now() + record.stability * MS_PER_DAY;
        return record;
    }

    // Good or Hard: advance through relearning steps
    const nextStep = (record.learningStep || 0) + 1;

    if (nextStep >= LEARNING_STEPS_MS.length) {
        // Graduate back to review
        record.state = ItemState.REVIEW;
        record.nextReviewDue = Date.now() + record.stability * MS_PER_DAY;
    } else {
        record.learningStep = nextStep;
        record.nextReviewDue = Date.now() + LEARNING_STEPS_MS[nextStep];
    }

    return record;
}

/**
 * Get all items that are due for review
 *
 * @param {object} itemHistory - Map of itemId → item record
 * @returns {Array} Items due for review, sorted by lowest retention first
 */
export function getDueItems(itemHistory) {
    const now = Date.now();
    const dueItems = [];

    Object.values(itemHistory).forEach(record => {
        if (!record.nextReviewDue) return;
        if (record.state === ItemState.NEW) return;

        if (record.nextReviewDue <= now) {
            const elapsedDays = (now - (record.lastReviewed || now)) / MS_PER_DAY;
            const retention = getRetention(record.stability, elapsedDays);
            dueItems.push({ ...record, currentRetention: retention });
        }
    });

    // Sort by lowest retention first (most urgent)
    dueItems.sort((a, b) => a.currentRetention - b.currentRetention);

    return dueItems;
}

/**
 * Count items due for review
 *
 * @param {object} itemHistory - Map of itemId → item record
 * @returns {number} Number of items due
 */
export function countDueItems(itemHistory) {
    return getDueItems(itemHistory).length;
}

/**
 * Select the next review item from the due queue
 *
 * Priority: (1) Due review items by lowest retention,
 *           (2) Learning/relearning items by nextReviewDue,
 *           (3) null if nothing is due
 *
 * @param {object} itemHistory - Map of itemId → item record
 * @param {Array} questionPool - All available questions
 * @param {Array} sessionAnsweredIds - IDs already answered this session
 * @returns {object|null} The selected question, or null
 */
export function selectNextReviewItem(itemHistory, questionPool, sessionAnsweredIds = []) {
    const now = Date.now();
    const answeredSet = new Set(sessionAnsweredIds);

    // Collect all due items (not yet answered this session)
    const candidates = [];

    Object.values(itemHistory).forEach(record => {
        if (answeredSet.has(record.itemId)) return;
        if (!record.nextReviewDue || record.state === ItemState.NEW) return;

        if (record.nextReviewDue <= now) {
            const elapsedDays = (now - (record.lastReviewed || now)) / MS_PER_DAY;
            const retention = getRetention(record.stability, elapsedDays);
            candidates.push({ record, retention, priority: 0 });
        }
    });

    if (candidates.length === 0) return null;

    // Sort: lowest retention first
    candidates.sort((a, b) => a.retention - b.retention);

    // Find the question object from the pool
    const selected = candidates[0].record;
    return questionPool.find(q => q.id === selected.itemId) || null;
}

/**
 * Get a summary of the review queue for display
 *
 * @param {object} itemHistory - Map of itemId → item record
 * @returns {{ dueNow: number, dueSoon: number, learning: number, totalTracked: number }}
 */
export function getReviewSummary(itemHistory) {
    const now = Date.now();
    const oneDayFromNow = now + MS_PER_DAY;

    let dueNow = 0;
    let dueSoon = 0;
    let learning = 0;
    let totalTracked = 0;

    Object.values(itemHistory).forEach(record => {
        if (record.state === ItemState.NEW) return;
        totalTracked++;

        if (record.state === ItemState.LEARNING || record.state === ItemState.RELEARNING) {
            learning++;
            if (record.nextReviewDue && record.nextReviewDue <= now) {
                dueNow++;
            }
            return;
        }

        if (record.nextReviewDue) {
            if (record.nextReviewDue <= now) dueNow++;
            else if (record.nextReviewDue <= oneDayFromNow) dueSoon++;
        }
    });

    return { dueNow, dueSoon, learning, totalTracked };
}
